/**
 * The auth state, as a React Context.
 *
 * Single point of integration between the browser, GoTrue, and the portal's own
 * `portal_users` table (read via PostgREST under RLS). Every component reads
 * auth state from here; no component calls supabase-js directly for auth.
 *
 * The activation gate is enforced at THREE layers (ADR-0003):
 *   1. React Router guard — convenience, not security
 *   2. BFF preHandler — the real enforcement for /bff/v1/*
 *   3. Postgres RLS — the real enforcement for /rest/v1/*
 *
 * Losing any one layer must not open the gate.
 *
 * The `documento` comes from the verified JWT and from nowhere else — never
 * from a query param, a body field, or a header (ADR-0004).
 *
 * This file is deliberately thin plumbing: session subscription, effect
 * wiring, and composing the injected dependencies (fetch, the Supabase
 * client) into the pure decision/orchestration modules below. None of the
 * policy lives here anymore —
 *   - the auth-guard decision           → `auth/guard.ts`
 *   - the stored-locale redirect        → `auth/localeRedirect.ts`
 *   - the BFF login/rotation calls      → `auth/loginFlow.ts`, `auth/rotateFlow.ts`
 *   - the portal_users read             → `api/portalUsers.ts`
 * — so each is testable without mounting this component.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useMatches, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeDocumento } from '@my-newshoes/documento';

import { getSupabaseClient, type Session, type User } from '../api/supabase.ts';
import { fetchPortalUser as fetchPortalUserRow, type PortalUser } from '../api/portalUsers.ts';
import { authGuardDecision } from './guard.ts';
import { localeRedirectDecision } from './localeRedirect.ts';
import { performLogin, type LoginResult } from './loginFlow.ts';
import { performRotatePassword, type RotateResult } from './rotateFlow.ts';
import { unsubscribeOnLogout } from '../push/pushSubscribe.ts';
import { currentLocale, type Locale } from '../i18n/index.ts';
import { buildPath, isRouteId, localeFromPathname } from '../i18n/routes.ts';

export type { PortalUser } from '../api/portalUsers.ts';
export type { LoginResult } from './loginFlow.ts';
export type { RotateError, RotateResult } from './rotateFlow.ts';
import { runtimeEnv } from '../runtimeConfig.ts';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthState {
  /** Loading: the initial session check has not finished yet. */
  isLoading: boolean;
  /** The current GoTrue session, null when unauthenticated. */
  session: Session | null;
  /** The GoTrue user object; null when loading or unauthenticated. */
  user: User | null;
  /** The portal's shadow row. Null until the fetch completes or if error. */
  portalUser: PortalUser | null;
  /** true when the JWT's `activated` claim is true AND portal_users confirms. */
  isActivated: boolean;

  error: string | null;

  login: (documento: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  rotatePassword: (newPassword: string) => Promise<RotateResult>;
  clearError: () => void;
  /**
   * Record the customer's language choice locally, before it is persisted.
   *
   * The stored-locale redirect below treats `portalUser.locale` as the truth
   * about what the customer wants, and re-evaluates it on every navigation. A
   * language switch navigates. So unless the local copy is updated in the same
   * event, the redirect sees the OLD stored value, decides the new URL is
   * wrong, and sends the customer straight back — the switch appears to do
   * nothing until a reload refetches the row. Call this before navigating.
   */
  setStoredLocale: (locale: Locale) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthState | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [portalUser, setPortalUser] = useState<PortalUser | null>(null);
  // Holds a CATALOGUE KEY, not text. Two reasons: switching language while an
  // error is on screen re-renders it translated, and — more importantly — the
  // four "invalid credentials" paths in `loginFlow.ts` physically cannot
  // drift apart into different wordings, because they are the same key. See
  // ADR-0003 and `loginFlow.ts`'s INVALID_CREDENTIALS_KEY.
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const user = session?.user ?? null;

  // Derive activation from the portal_users shadow row. The JWT claim is a
  // hint for fast routing; this is the authoritative check for the data layer.
  const isActivated = portalUser !== null && portalUser.activated_at !== null;

  // -----------------------------------------------------------------------
  // Fetch the portal_users row via PostgREST. Runs after auth state changes.
  // -----------------------------------------------------------------------
  const fetchPortalUser = useCallback(async (userId: string): Promise<void> => {
    const { data, error } = await fetchPortalUserRow(getSupabaseClient(), userId);
    if (error) {
      // Surface the error but don't crash — the gate will stay closed and
      // the user sees a degraded experience rather than a blank screen.
      console.error('Failed to fetch portal_users row:', error);
    }
    setPortalUser(data);
  }, []);

  // -----------------------------------------------------------------------
  // Listen for auth state changes.
  // -----------------------------------------------------------------------
  useEffect(() => {
    const client = getSupabaseClient();
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange(async (_event, newSession) => {
      setSession(newSession);

      if (newSession?.user) {
        await fetchPortalUser(newSession.user.id);
      } else {
        setPortalUser(null);
      }

      setIsLoading(false);
    });

    // Also fetch the initial session. onAuthStateChange fires once on mount
    // but the timing can be unpredictable — force a check.
    void client.auth.getSession().then(({ data }) => {
      if (data.session) {
        setSession(data.session);
        void fetchPortalUser(data.session.user.id);
      }
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchPortalUser]);

  // -----------------------------------------------------------------------
  // login
  // -----------------------------------------------------------------------
  const login = useCallback(async (rawDocumento: string, password: string): Promise<LoginResult> => {
    setErrorKey(null);

    const canonical = normalizeDocumento(rawDocumento);
    const bffBase = runtimeEnv('VITE_BFF_BASE_URL') ?? '/bff/v1';

    const outcome = await performLogin(canonical, password, {
      fetchImpl: fetch,
      bffBase,
      setSession: (tokens) => getSupabaseClient().auth.setSession(tokens),
    });

    setErrorKey(outcome.errorKey);
    return outcome.result;
  }, []);

  // -----------------------------------------------------------------------
  // logout
  // -----------------------------------------------------------------------
  const logout = useCallback(async (): Promise<void> => {
    setErrorKey(null);

    // A customer must not keep receiving notifications after logging out
    // (product decision, 2026-07-31). BEFORE signOut, never after: RLS resolves
    // `auth.uid()` from the session, so a delete attempted afterwards silently
    // affects nothing and leaves the row alive — which is how this device kept
    // delivering the previous customer's notifications to whoever logged in
    // next. Best-effort: logging out is never allowed to fail on this.
    await unsubscribeOnLogout(getSupabaseClient());

    const { error: signOutError } = await getSupabaseClient().auth.signOut();
    if (signOutError) {
      console.error('Sign-out error:', signOutError.message);
    }
    // The session listener will clear state. Force a redirect regardless.
    setSession(null);
    setPortalUser(null);
    // Nothing fetched for this customer may survive into the next one's
    // session on a shared phone — and a cached 401 from a revoked session
    // must not survive into a fresh one either.
    queryClient.clear();
    navigate(buildPath('login', currentLocale()), { replace: true });
  }, [navigate, queryClient]);

  // -----------------------------------------------------------------------
  // rotatePassword — the activation gate
  // -----------------------------------------------------------------------
  const rotatePassword = useCallback(
    async (newPassword: string): Promise<RotateResult> => {
      setErrorKey(null);

      if (!session?.user) {
        return { ok: false, error: 'session_expired' };
      }

      if (isActivated) {
        // Should be unreachable (router guard blocks it), but enforce anyway.
        return { ok: false, error: 'not_activated' };
      }

      // Call the BFF rotation endpoint. The BFF verifies the JWT, validates
      // the password, updates GoTrue, and marks portal_users.activated_at.
      // We do NOT call supabase.auth.updateUser directly — the activation
      // status update in portal_users must happen atomically, server-side.
      const bffBase = runtimeEnv('VITE_BFF_BASE_URL') ?? '/bff/v1';

      const result = await performRotatePassword(newPassword, {
        fetchImpl: fetch,
        bffBase,
        accessToken: session.access_token,
        refetchPortalUser: () => fetchPortalUser(session.user.id),
        installSession: async (tokens) => {
          const { error } = await getSupabaseClient().auth.setSession(tokens);
          if (error) throw error;
        },
      });

      if (result.ok) {
        // Anything cached under the pre-rotation token (revoked server-side
        // the moment the BFF answered) is stale by definition.
        queryClient.clear();

        if (!result.reauthenticated) {
          // The password changed, but the browser holds a DEAD session — the
          // BFF revokes every session on rotation and no replacement arrived.
          // Carrying on would render empty service-order screens until the
          // customer logged out on their own. Sign out honestly, and say why
          // on the login screen. (`logout` resets errorKey first, so set it
          // after.)
          await logout();
          setErrorKey('error.passwordChangedSignInAgain');
        }
      }
      return result;
    },
    [session, isActivated, fetchPortalUser, logout, queryClient],
  );

  // -----------------------------------------------------------------------
  // clearError
  // -----------------------------------------------------------------------
  const clearError = useCallback(() => setErrorKey(null), []);

  // Optimistic on purpose: if the RPC that persists this fails, the UI keeps
  // the language the customer asked for and the choice simply does not follow
  // them to another device. Reverting would yank the page back into the old
  // language over a background write they never saw.
  const setStoredLocale = useCallback((locale: Locale): void => {
    setPortalUser((prev) => (prev ? { ...prev, locale } : prev));
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      isLoading,
      session,
      user,
      portalUser,
      isActivated,
      error: errorKey === null ? null : t(errorKey),
      login,
      logout,
      rotatePassword,
      clearError,
      setStoredLocale,
    }),
    [isLoading, session, user, portalUser, isActivated, errorKey, t, login, logout, rotatePassword, clearError, setStoredLocale],
  );

  const { pathname } = useLocation();
  const matches = useMatches();
  const params = useParams();

  // The language the address bar is asking for. It is the source of truth for
  // what renders (ADR-0015); everything below is expressed in terms of it so a
  // guard redirect can never strand the customer in the other language.
  const urlLocale = localeFromPathname(pathname) ?? currentLocale();

  // Read off the route `handle` rather than parsed out of the pathname, so a
  // slug rename cannot silently break either redirect below.
  const handle = matches[matches.length - 1]?.handle;
  const routeId =
    typeof handle === 'object' && handle !== null && 'routeId' in handle && isRouteId(handle.routeId)
      ? handle.routeId
      : undefined;

  // -----------------------------------------------------------------------
  // Apply the customer's stored language once their row arrives. Decision is
  // pure — see `localeRedirect.ts` for why this redirects rather than just
  // swapping the catalogue.
  // -----------------------------------------------------------------------
  useEffect(() => {
    const target = localeRedirectDecision({
      storedLocale: portalUser?.locale,
      pathname,
      routeId,
      params: { osId: params['osId'] },
    });
    if (target) navigate(target, { replace: true });
  }, [portalUser?.locale, pathname, routeId, params, navigate]);

  // -----------------------------------------------------------------------
  // Auth-driven navigation — redirect based on auth state. Decision is pure
  // — see `guard.ts` for the security reasoning.
  // Runs INSIDE the React tree so it has access to the real auth state,
  // unlike loaders which run once before render.
  // -----------------------------------------------------------------------
  useEffect(() => {
    const target = authGuardDecision({ isLoading, session, isActivated, pathname, locale: urlLocale });
    if (target) navigate(target, { replace: true });
  }, [isLoading, session, isActivated, pathname, navigate, urlLocale]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within <AuthProvider>');
  }
  return ctx;
}
